import { Button, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

const CustomAgDateFilter = (props: any) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [value, setValue] = useState(''); // current date string
  const [isActive, setIsActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle typing in floating filter input
  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);

      props.parentFilterInstance?.((instance: any) => {
        // For date filter, default to 'equals'
        instance.onFloatingFilterChanged('equals', newValue);
      });
    },
    [props],
  );

  useEffect(() => {
    if (!props.model) setValue('');
  }, [props.model]);

  // Sync filter model changes (popup or programmatic)
  useEffect(() => {
    const updateFromModel = () => {
      const filterModel = props.api.getFilterModel?.();
      const colId = props.column.getColId?.();
      const model = filterModel?.[colId];

      // Support both custom { dateBetween: { from, to } } and AG Grid inRange { type: 'inRange', dateFrom, dateTo }
      const hasCustomBetween = model?.dateBetween?.from && model?.dateBetween?.to;
      const isAgInRange = model?.type === 'inRange' && model?.dateFrom && model?.dateTo;

      if (hasCustomBetween) {
        const from = model.dateBetween.from;
        const to = model.dateBetween.to;
        setValue(`${from}|${to}`);
        setIsActive(true);
      } else if (isAgInRange) {
        setValue(`${model.dateFrom}|${model.dateTo}`);
        setIsActive(true);
      } else if (model?.dateFrom) {
        setValue(model.dateFrom);
        setIsActive(true);
      } else {
        setValue('');
        setIsActive(false);
      }
    };

    // Initial sync
    updateFromModel();

    // Listen to filter changes
    props.api?.addEventListener('filterChanged', updateFromModel);
    return () => {
      props.api?.removeEventListener('filterChanged', updateFromModel);
    };
  }, [props.api, props.column, props.model]);

  const onFilterIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    props.showParentFilter?.(inputRef.current);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
      <Stack
        direction="row"
        sx={{
          border: `1px solid ${theme.palette.borderLight}`,
          borderRadius: `${theme.borderRadius.sm}px`,
          backgroundColor: theme.palette.common.white,
          width: '100%',
          height: '25px',
          gap: '2px',
          overflow: 'hidden',
        }}
        alignItems={'center'}
      >
        {(() => {
          // Compute a formatted display string for single date or range
          let displayValue = '';
          if (value && typeof value === 'string' && value.includes('|')) {
            const [from, to] = value.split('|');
            displayValue = `${dayjs(from).format('DD/MM/YYYY')} - ${dayjs(to).format('DD/MM/YYYY')}`;
          } else if (value) {
            displayValue = dayjs(value).format('DD/MM/YYYY');
          }

          return (
            <TextField
              value={displayValue}
              onChange={onInputChange}
              onClick={onFilterIconClick}
              inputRef={inputRef}
              aria-readonly
              placeholder={t('common.select')}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  padding: 0,
                  boxShadow: 'none',
                  fontSize: '12px',
                  paddingLeft: '5px',
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' },
                },
                '& .MuiOutlinedInput-input': {
                  padding: 0,
                  height: '20px',
                  cursor: 'pointer',
                },
              }}
            />
          );
        })()}

        {/* Filter icon */}
        <Button
          type="button"
          className="ag-icon ag-icon-filter"
          sx={{
            cursor: 'pointer',
            fontSize: '14px',
            height: '100%',
            width: '20px',
            minWidth: '30px',
            padding: 0,
            borderRadius: '0px',
            backgroundColor: isActive ? theme.palette.leftNavigation.activeBg : 'transparent',
            color: isActive ? theme.palette.primary.main : theme.palette.textMedium,
            transition: `all ${theme.transitions.custom.fast}`,
            '&:hover': {
              backgroundColor: isActive
                ? theme.palette.leftNavigation.activeBg
                : 'rgba(0,0,0,0.05)',
            },
          }}
          onClick={onFilterIconClick}
        />
      </Stack>
    </Box>
  );
};

export default CustomAgDateFilter;
